<template>
  <main class="application-shell">
    <div ref="container" class="map-surface" aria-label="Flood simulation map"></div>

    <ControlSidebar
      :labels="labels"
      :locale="locale"
      :dataset-id="datasetId"
      :dataset-name="dataset?.manifest.name || ''"
      :radius="sourceRadiusMeters"
      :flow-rate="sourceFlowRate"
      :speed="speed"
      :threshold="threshold"
      :click-placement="clickPlacementEnabled"
      :paused="status.paused"
      :ready="status.ready"
      :source-count="sources.length"
      :sources="sources"
      :webgl2="status.webgl2"
      :float-framebuffer="status.floatFramebuffer"
      :statistics="statistics"
      @toggle-locale="toggleLocale"
      @change-dataset="changeDataset"
      @update:radius="sourceRadiusMeters = $event"
      @update:flow-rate="sourceFlowRate = $event"
      @update:speed="updateSpeed"
      @update:threshold="updateThreshold"
      @add-default="addDefaultSource"
      @toggle-placement="toggleClickPlacement"
      @toggle-pause="togglePause"
      @reset="reset"
      @clear="clearSources"
      @update-source="updateSource"
      @remove-source="removeSource"
      @export="exportResult($event, threshold)"
    />

    <div class="top-status" :class="{ ready: status.ready }">
      <span class="status-dot"></span>
      {{ status.loading ? labels.loading : status.ready ? labels.live : labels.unavailable }}
    </div>

    <div v-if="status.error" class="error-toast" role="alert">
      <TriangleAlert :size="18" />
      <span>{{ status.error }}</span>
      <button type="button" :title="labels.dismiss" @click="status.error = ''">
        <X :size="16" />
      </button>
    </div>

    <footer class="scientific-notice">
      <ShieldAlert :size="15" />
      {{ labels.disclaimer }}
    </footer>
  </main>
</template>

<script setup lang="ts">
import { ShieldAlert, TriangleAlert, X } from '@lucide/vue';
import { computed, onMounted, ref } from 'vue';
import ControlSidebar from './components/ControlSidebar.vue';
import { useFloodMap } from './composables/useFloodMap';

const {
  container,
  dataset,
  sources,
  statistics,
  status,
  clickPlacementEnabled,
  sourceRadiusMeters,
  sourceFlowRate,
  initialize,
  addDefaultSource,
  toggleClickPlacement,
  updateSource,
  removeSource,
  clearSources,
  reset,
  togglePause,
  setSpeed,
  setThreshold,
  exportResult
} = useFloodMap();

const locale = ref<'en' | 'zh'>('en');
const datasetId = ref('boulder-canyon-3dep');
const speed = ref(10);
const threshold = ref(0.01);

const translations = {
  en: {
    subtitle: 'Shallow-water research workbench',
    language: '切换到中文',
    dataset: 'Terrain dataset',
    syntheticBasin: 'Synthetic basin benchmark',
    radius: 'Source radius',
    flow: 'Inflow rate',
    speed: 'Simulation speed',
    threshold: 'Visible depth',
    addSource: 'Add reference inflow',
    mapPlacement: 'Map placement',
    clickToPlace: 'Click DEM to add',
    pause: 'Pause',
    resume: 'Resume',
    reset: 'Reset water',
    clearSources: 'Clear inflows',
    activeSources: 'Active inflows',
    sourceLabel: 'Inflow',
    removeSource: 'Remove inflow',
    radiusShort: 'Radius',
    flowShort: 'Flow',
    exports: 'Export',
    depth: 'Depth',
    extent: 'Extent',
    report: 'Report',
    runStatus: 'Simulation',
    elapsed: 'Model time',
    secondsUnit: 's',
    minutesUnit: 'min',
    hoursUnit: 'h',
    wetCells: 'Wet cells',
    maxDepth: 'Maximum depth',
    volume: 'Water volume',
    loading: 'Loading terrain',
    live: 'Simulation ready',
    unavailable: 'Unavailable',
    dismiss: 'Dismiss',
    disclaimer: 'Research and education prototype. Not an operational forecast or warning service.'
  },
  zh: {
    subtitle: '浅水模拟科研工作台',
    language: 'Switch to English',
    dataset: '地形数据',
    syntheticBasin: '合成流域基准',
    radius: '源点半径',
    flow: '注入流量',
    speed: '模拟速度',
    threshold: '可见水深',
    addSource: '添加参考入流',
    mapPlacement: '地图布点',
    clickToPlace: '点击 DEM 添加',
    pause: '暂停',
    resume: '继续',
    reset: '重置水体',
    clearSources: '清空入流',
    activeSources: '活动入流',
    sourceLabel: '入流',
    removeSource: '删除入流',
    radiusShort: '半径',
    flowShort: '流量',
    exports: '导出',
    depth: '水深',
    extent: '范围',
    report: '报告',
    runStatus: '模拟状态',
    elapsed: '模型时间',
    secondsUnit: '秒',
    minutesUnit: '分',
    hoursUnit: '时',
    wetCells: '湿润网格',
    maxDepth: '最大水深',
    volume: '水体体积',
    loading: '正在加载地形',
    live: '模拟已就绪',
    unavailable: '不可用',
    dismiss: '关闭',
    disclaimer: '科研教学原型，不是正式洪水预报或灾害预警服务。'
  }
} as const;

const labels = computed<Record<string, string>>(() => translations[locale.value]);

onMounted(() => initialize(datasetId.value));

function toggleLocale(): void {
  locale.value = locale.value === 'en' ? 'zh' : 'en';
}

async function changeDataset(id: string): Promise<void> {
  datasetId.value = id;
  await initialize(id);
  setSpeed(speed.value);
  setThreshold(threshold.value);
}

function updateSpeed(value: number): void {
  speed.value = value;
  setSpeed(value);
}

function updateThreshold(value: number): void {
  threshold.value = value;
  setThreshold(value);
}
</script>
